import React, { useState, useMemo, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Box, Typography, Grid, CircularProgress, TextField
} from "@mui/material";
import CardHeader from "@/components/card/CardHeader";
import { Form, Formik } from "formik";
import { useTheme } from "@mui/styles";
import { useApproveRegularizationMutation } from "@/api/services/attendance/regularization/EmployeeRegularization";
import { toast } from "@/components/toastify";
import { StyledButton } from "@/components";
import CommonDataTable from "@/components/dataTableComponents/NewDatatable";

const AdminAttendanceRegApproved = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const [approveRegularization] = useApproveRegularizationMutation();

  const partialData = location.state?.data || {};
  const [approvalState, setApprovalState] = useState({});
  const [validationErrors, setValidationErrors] = useState({});

  const rows = useMemo(() => {
    return partialData?.appliedRegularizations?.map((item, index) => ({
      sno: index + 1,
      date: item.attendanceDate,
      empId: item.empId,
      regCode: item.regularizationCode,
      decision: approvalState[item.attendanceDate]?.decision || "Approved",
      remarks: approvalState[item.attendanceDate]?.remarks || "",
      shift: item.shift,
      startTime: item.startTime,
      endTime: item.endTime,
      reason: item.reason,
    })) || [];
  }, [partialData, approvalState]);

  const updateValidation = useCallback((date, remarks, decision) => {
    setValidationErrors(prev => {
      const updated = { ...prev };
      if (decision === "Rejected" && !remarks.trim()) {
        updated[date] = "Remarks required for rejected date.";
      } else {
        delete updated[date];
      }
      return updated;
    });
  }, []);

  const handleDecisionToggle = useCallback((attendanceDate, currentDecision) => {
    const newDecision = currentDecision === "Approved" ? "Rejected" : "Approved";

    setApprovalState(prev => {
      const currentRemarks = prev[attendanceDate]?.remarks || "";
      const updatedState = {
        ...prev,
        [attendanceDate]: {
          ...prev[attendanceDate],
          decision: newDecision,
        },
      };
      updateValidation(attendanceDate, currentRemarks, newDecision);
      return updatedState;
    });
  }, [updateValidation]);

  const handleRemarksChange = useCallback((date, remark) => {
    setApprovalState(prev => {
      const decision = prev[date]?.decision || "Approved";
      const updatedState = {
        ...prev,
        [date]: { ...prev[date], remarks: remark },
      };
      updateValidation(date, remark, decision);
      return updatedState;
    });
  }, [updateValidation]);

  const columns = useMemo(() => [
    { field: "date", headerName: "Date", flex: 1 },
    {
      field: "decision",
      headerName: "Approve/Reject",
      flex: 1,
      renderCell: (params) => {
        const attendanceDate = params.row.date;
        const currentDecision = approvalState[attendanceDate]?.decision || "Approved";

        return (
          <Box
            sx={{
              backgroundColor: currentDecision === "Approved" ? "green" : "white",
              color: currentDecision === "Approved" ? "white" : "red",
              padding: "5px 10px",
              borderRadius: "5px",
              cursor: "pointer",
              display: "inline-block",
              userSelect: "none",
            }}
            onClick={() => handleDecisionToggle(attendanceDate, currentDecision)}
          >
            {currentDecision === "Approved" ? "✅ Approved" : "❌ Rejected"}
          </Box>
        );
      }
    },
    {
      field: "remarks",
      headerName: "Approver Remarks",
      flex: 1,
      renderCell: (params) => {
        const attendanceDate = params.row.date;
        const error = validationErrors[attendanceDate];
        const remarks = approvalState[attendanceDate]?.remarks || "";

        return (
          <Box>
            <TextField
              multiline
              fullWidth
              minRows={2}
              variant="outlined"
              size="small"
              value={remarks}
              onChange={(e) => handleRemarksChange(attendanceDate, e.target.value)}
              error={Boolean(error)}
              helperText={error}
              onKeyDown={(e) => {
                // Prevent DataGrid from capturing space key
                e.stopPropagation();
              }}
            />
          </Box>
        );
      }
    },
    { field: "shift", headerName: "Shift", flex: 1 },
    { field: "startTime", headerName: "In Time", flex: 1 },
    { field: "endTime", headerName: "Out Time", flex: 1 },
    { field: "reason", headerName: "Reason", flex: 1 }
  ], [rows, approvalState, validationErrors, handleDecisionToggle, handleRemarksChange]);


  const handleFormSubmit = async () => {
    const errors = {};
    const approved = [], rejected = [], remarks = [];

    partialData?.appliedRegularizations?.forEach((item) => {
      const date = item.attendanceDate;
      const state = approvalState[date] || {};
      const decision = state.decision || "Approved";
      const remark = state.remarks || "";

      if (decision === "Rejected" && !remark.trim()) {
        errors[date] = "Remarks required for rejected date.";
      }

      if (decision === "Approved") approved.push(date);
      else rejected.push(date);

      remarks.push(remark);
    });

    // Object.entries(approvalState).forEach(([date, data]) => {
    //   const decision = data?.decision || "Approved";
    //   const remark = data?.remarks || "";

    //   console.log(decision,"tets");

    //   if (decision === "Rejected" && !remark.trim()) {
    //     errors[date] = "Remarks required for rejected date.";
    //   }

    //   if (decision === "Approved") approved.push(date);
    //   else rejected.push(date);

    //   remarks.push(remark);
    // });

    setValidationErrors(errors);
    if (Object.keys(errors).length > 0) return;

    const payload = {
      empId: partialData?.empId,
      regCode: partialData?.regularizationCode,
      key: "Partially Approved",
      approvedDate: approved,
      rejectedDate: rejected,
      remarks
    };

    try {
      await approveRegularization(payload).unwrap();
      toast("success", "Approved Successfully!");
      navigate("/attendance/approvals");
    } catch (err) {
      console.error("Approval API Error:", err);
    }
  };

  return (
    <CardHeader header="Applied Date Regularization">
      <Typography
        variant="h3"
        sx={{
          fontSize: "16px !important",
          fontWeight: "900",
          marginBottom: "20px",
        }}
      >
        EMPLOYEE REMARKS : Permission
      </Typography>

      <CommonDataTable columns={columns} rows={rows} />

      <Box display="flex" justifyContent="flex-end" mt={5}>
        <Formik initialValues={{ approveremarks: "", approve: false }} onSubmit={handleFormSubmit}>
          {({ isSubmitting }) => (
            <Form>
              <Grid container spacing={2} sx={{ display: "flex", gap: 2 }}>
                <StyledButton
                  type="submit"
                  label={isSubmitting ? <CircularProgress size={24} color="inherit" /> : "Submit"}
                  variant="contained"
                />
                <StyledButton
                  type="reset"
                  label="Cancel"
                  variant="outlined"
                  onClick={() => navigate("/attendance/approvals")}
                />
              </Grid>
            </Form>
          )}
        </Formik>
      </Box>
    </CardHeader>
  );
};

export default AdminAttendanceRegApproved;
